import { useSearchParams, useNavigate, useParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { BellRing, Check, CheckCheck, Clock3, ExternalLink, Hash, Lock, MessageSquareReply, Slack as SlackIcon, Users } from 'lucide-react'
import { apiPost } from '@/api/axios-client'
import { EmptyState, PageHeader } from '@/components/composites/states'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { formatExactDateTime, formatRelativeTime } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { commsKeys, snoozePresets, useCommsItems, useSetCommsStatus, useTapworkNotifications, type CommsItem, type CommsView } from './api'

const VIEWS: Array<{ id: CommsView; label: string }> = [
  { id: 'open', label: 'Needs a response' },
  { id: 'snoozed', label: 'Snoozed' },
  { id: 'done', label: 'Handled' },
]

/**
 * Where you have been tagged and have not yet answered. Slack mentions first, in the channels the
 * Tapwork bot is in; other channels will arrive in the same list. Beside them, Tapwork's own
 * notifications, so there is one place to look for "what is waiting on me".
 */
export function CommsPage() {
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'notifications' ? 'notifications' : 'mentions'
  const view = (VIEWS.find((v) => v.id === params.get('view'))?.id ?? 'open') as CommsView
  const set = (key: string, value: string) => setParams((prev) => { const next = new URLSearchParams(prev); next.set(key, value); return next }, { replace: true })
  return (
    <div className="mx-auto w-full max-w-[900px] px-2 pb-10 md:px-6">
      <PageHeader kicker="Communications" title="Messages waiting on you" description="Where you have been tagged, so you can acknowledge it and answer. Slack today; more channels later." />
      <div className="mb-3 flex flex-wrap items-center gap-1 border-b border-border">
        {([['mentions', 'Mentions'], ['notifications', 'Tapwork notifications']] as const).map(([id, label]) => (
          <button key={id} type="button" onClick={() => set('tab', id)} className={cn('-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors', tab === id ? 'border-brand text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>{label}</button>
        ))}
      </div>
      {tab === 'mentions' ? (
        <>
          <div className="mb-3 flex gap-1">
            {VIEWS.map((v) => (
              <button key={v.id} type="button" onClick={() => set('view', v.id)} className={cn('rounded-full px-3 py-1 text-xs font-medium transition-colors', view === v.id ? 'bg-brand text-text-inverse' : 'bg-surface-sunken text-muted-foreground hover:text-foreground')}>{v.label}</button>
            ))}
          </div>
          <Mentions view={view} />
        </>
      ) : (
        <Notifications />
      )}
    </div>
  )
}

function Mentions({ view }: { view: CommsView }) {
  const query = useCommsItems(view)
  const items = query.data?.items ?? []
  if (query.isLoading) return <div className="grid gap-2"><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
  if (query.isError) return <p className="text-sm text-danger">Your messages could not be loaded. Try again in a moment.</p>
  if (items.length === 0) {
    return (
      <EmptyState
        title={view === 'open' ? 'Nothing waiting on you' : view === 'snoozed' ? 'Nothing snoozed' : 'Nothing handled yet'}
        description={view === 'open' ? 'When someone tags you in a Slack channel where Tapwork is, it shows up here until you answer.' : undefined}
      />
    )
  }
  return <ul className="grid gap-2">{items.map((item) => <MentionRow key={item.id} item={item} />)}</ul>
}

function MentionRow({ item }: { item: CommsItem }) {
  const setStatus = useSetCommsStatus()
  const where = item.conversationKind === 'group_dm' ? 'a group message' : item.conversationName ? `#${item.conversationName}` : 'a channel'
  const Where = item.conversationKind === 'private' ? Lock : item.conversationKind === 'group_dm' ? Users : Hash
  const act = (status: Parameters<typeof setStatus.mutate>[0]['status'], snoozeUntil?: string) => setStatus.mutate({ id: item.id, status, snoozeUntil })
  const acknowledged = item.status === 'acknowledged'
  return (
    <li className={cn('rounded-xl border bg-background p-4', item.status === 'new' ? 'border-brand/40' : 'border-border')}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg bg-surface-sunken text-muted-foreground"><SlackIcon className="size-4" aria-hidden /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm">
            <span className="font-semibold">{item.fromName ?? 'Someone'}</span> mentioned you in{' '}
            <span className="inline-flex items-center gap-0.5 font-medium"><Where className="size-3.5" aria-hidden />{where.replace(/^#/, '')}</span>
            {item.status === 'new' ? <span className="ml-2 rounded-full bg-brand px-1.5 py-0.5 align-middle text-micro font-semibold text-text-inverse">New</span> : null}
            {acknowledged ? <span className="ml-2 inline-flex items-center gap-0.5 align-middle text-xs text-success"><Check className="size-3" />Acknowledged</span> : null}
            {item.status === 'replied' ? <span className="ml-2 align-middle text-xs text-success">Replied</span> : null}
          </p>
          <time className="text-xs text-muted-foreground" title={formatExactDateTime(item.happenedAt)}>{formatRelativeTime(item.happenedAt)}{item.status === 'snoozed' && item.snoozedUntil ? ` · back ${formatRelativeTime(item.snoozedUntil)}` : ''}</time>
          {item.body ? <p className="mt-1.5 line-clamp-3 whitespace-pre-wrap break-words text-sm text-muted-foreground">{item.body}</p> : <p className="mt-1.5 text-xs italic text-muted-foreground">From a private conversation — open it in Slack to read it.</p>}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {item.permalink ? (
              <Button size="sm" variant="outline" asChild><a href={item.permalink} target="_blank" rel="noopener noreferrer"><ExternalLink className="size-3.5" />Open in Slack</a></Button>
            ) : null}
            {item.status === 'new' ? <Button size="sm" variant="outline" disabled={setStatus.isPending} onClick={() => act('acknowledged')}><Check className="size-3.5" />Acknowledge</Button> : null}
            {item.status !== 'replied' && item.status !== 'done' ? <Button size="sm" variant="outline" disabled={setStatus.isPending} onClick={() => act('replied')}><MessageSquareReply className="size-3.5" />Mark replied</Button> : null}
            {item.status !== 'replied' && item.status !== 'done' ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" disabled={setStatus.isPending}><Clock3 className="size-3.5" />Snooze</Button></DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {snoozePresets().map((p) => <DropdownMenuItem key={p.label} onSelect={() => act('snoozed', p.at)}>{p.label}</DropdownMenuItem>)}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {item.status !== 'done' && item.status !== 'replied' ? <Button size="sm" variant="ghost" disabled={setStatus.isPending} onClick={() => act('done')}><CheckCheck className="size-3.5" />Done</Button> : <Button size="sm" variant="ghost" disabled={setStatus.isPending} onClick={() => act('new')}>Reopen</Button>}
          </div>
        </div>
      </div>
    </li>
  )
}

function Notifications() {
  const query = useTapworkNotifications()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { workspaceSlug } = useParams()
  const items = query.data?.items ?? []
  if (query.isLoading) return <div className="grid gap-2"><Skeleton className="h-16" /><Skeleton className="h-16" /></div>
  if (query.isError) return <p className="text-sm text-danger">Notifications could not be loaded.</p>
  if (items.length === 0) return <EmptyState title="No notifications" />
  const open = async (n: (typeof items)[number]) => {
    if (!n.readAt) {
      await apiPost('notify/inbox/read', { ids: [n.id] }).catch(() => undefined)
      void queryClient.invalidateQueries({ queryKey: commsKeys.all })
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    }
    if (n.href && workspaceSlug) navigate(`/w/${workspaceSlug}${n.href}`)
  }
  return (
    <ul className="grid gap-1.5">
      {items.map((n) => (
        <li key={n.id}>
          <button type="button" onClick={() => void open(n)} className={cn('flex w-full items-start gap-3 rounded-xl border p-3 text-left hover:bg-accent/50', n.readAt ? 'border-border' : 'border-brand/40 bg-brand/5')}>
            <BellRing className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className={cn('block text-sm', !n.readAt && 'font-semibold')}>{n.title}</span>
              {n.body ? <span className="block truncate text-xs text-muted-foreground">{n.body}</span> : null}
            </span>
            <time className="shrink-0 text-xs text-muted-foreground" title={formatExactDateTime(n.createdAt)}>{formatRelativeTime(n.createdAt)}</time>
          </button>
        </li>
      ))}
    </ul>
  )
}
